import { Component, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar } from '@angular/material/snack-bar';
import { PatientSelectorComponent, PatientSource } from '../../shared/components/patient-selector/patient-selector.component';
import { isCustomSubject, Subject } from '../../_interfaces/subject';
import { Visit } from '../../_interfaces/visit';
import { ManualPatientSelectorComponent } from '../manual-patient-selector/manual-patient-selector.component';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { EncounterService } from '../../_services/encounter.service';
import { FeatureFlagsService } from '../../_services/feature-flags.service';
import { canSelectVisit, continueWithOptionalVisit } from '../../_helpers/visit-selection';
import { RegenerateAfterEditComponent, RegenerateAfterEditResult } from '../../shared/dialogs/regenerate-after-edit/regenerate-after-edit.component';
import { firstValueFrom } from 'rxjs';
import { AppDialogRef } from '../../shared/components/dialog/dialog-ref';
import { APP_DIALOG_DATA } from '../../shared/components/dialog/dialog.tokens';
import { DialogService } from '../../shared/components/dialog/dialog.service';
import { DIALOG_IMPORTS } from '../../shared/components/dialog/dialog.imports';

@Component({
  selector: 'app-edit-patient',
  standalone: true,
  imports: [CommonModule, ...DIALOG_IMPORTS, MatButtonModule, MatIconModule, PatientSelectorComponent, TranslateModule],
  templateUrl: 'edit-patient.component.html',
})
export class EditPatientComponent {
  selectedPatient: Subject | null = null;
  selectedPatientSource: PatientSource | null = null;
  isSubmitting: boolean = false;

  constructor(
    private dialogRef: AppDialogRef<EditPatientComponent>,
    private dialog: DialogService,
    @Inject(APP_DIALOG_DATA) public data: {
      dictationId: string;
      patient?: any;
      mode: 'consultation' | 'dictation';
      flow: 'encounter' | 'document';
      promptRegenerateAfterEdit?: boolean;
    },
    private encounterService: EncounterService,
    private snackBar: MatSnackBar,
    private translate: TranslateService,
    private flags: FeatureFlagsService
  ) {}

  get headerLabel(): string {
    return this.data?.patient ? 'Edit patient' : 'Select patient';
  }

  get isVisitSelectionEnabled(): boolean {
    return this.flags.isEnabled('visitSelection');
  }

  get primaryActionLabel(): string {
    return this.isVisitSelectionEnabled ? 'Continue' : 'Update patient';
  }

  get isCustom(): boolean {
    if (!this.data?.patient) return false;
    return isCustomSubject(this.data.patient);
  }

  async onPatientSelected(patient: Subject | null): Promise<void> {
    this.selectedPatient = patient;
  }

  selectPatientSource(source: PatientSource | null): void {
    this.selectedPatientSource = source;
  }

  async onContinue(): Promise<void> {
    if (!this.selectedPatient) return;
    if (!canSelectVisit(this.isVisitSelectionEnabled, this.selectedPatientSource, this.selectedPatient)) {
      await this.updatePatient(this.selectedPatient, null);
      return;
    }
    continueWithOptionalVisit({
      dialog: this.dialog,
      dialogRef: this.dialogRef,
      patient: this.selectedPatient,
      mode: this.data.mode,
      flow: this.data.flow,
      source: this.selectedPatientSource,
      isVisitSelectionEnabled: this.isVisitSelectionEnabled,
      onCommit: (visit) => this.updatePatient(this.selectedPatient!, visit),
    });
  }

  async updatePatient(patient: Subject | null, visit: Visit | null): Promise<void> {
    const regenerateAfterEdit = await this.confirmDocumentRegenerateIfNeeded();
    if (this.data.flow === 'document'
      && this.data.promptRegenerateAfterEdit !== false
      && (regenerateAfterEdit === 'close' || regenerateAfterEdit === undefined)) {
      this.close();
      return;
    }

    this.isSubmitting = true;
    this.dialogRef.disableClose = true;
    try {
      const result = await this.encounterService.updatePatient(this.data.dictationId, patient, { visit });
      if (result?.saved) {
        const payload = regenerateAfterEdit ? { ...result, regenerateAfterEdit } : result;
        if (canSelectVisit(this.isVisitSelectionEnabled, this.selectedPatientSource, this.selectedPatient)) {
          this.dialogRef.dismiss(payload);
        } else {
          this.dialogRef.close(payload);
        }
      } else {
        this.dialog.show(this.dialogRef);
      }
    } catch {
      this.dialog.show(this.dialogRef);
    } finally {
      this.dialogRef.disableClose = false;
      this.isSubmitting = false;
    }
  }

  private async confirmDocumentRegenerateIfNeeded(): Promise<RegenerateAfterEditResult | undefined> {
    if (this.data.flow !== 'document' || this.data.promptRegenerateAfterEdit === false) {
      return undefined;
    }

    const regenerateDialogRef = this.dialog.open(RegenerateAfterEditComponent, {size: 'md'});
    return firstValueFrom(regenerateDialogRef.afterClosed());
  }

  openManualPatient(): void {
    const data: Record<string, unknown> = {
      from: 'edit',
      dictationId: this.data.dictationId,
      flow: this.data.flow,
      promptRegenerateAfterEdit: this.data.promptRegenerateAfterEdit,
    };

    // Only pre-populate fields when editing a *custom* (manual) patient.
    // Custom patients have no facility_id (matching Flutter's Patient.isCustom).
    if (this.data?.patient && this.isCustom) {
      data['patientName'] = this.data.patient.subject_name ?? '';
      data['patientId'] = this.data.patient.subject_id || '';
      data['dateOfBirth'] = this.data.patient.subject_dob || null;
    }

    const childRef = this.dialog.open(ManualPatientSelectorComponent, {
      size: 'sm',
      data,
    });

    childRef.beforeClosed().subscribe((res) => {
      if (res === 'back') {
        return;
      }
      if (res === 'close') {
        this.dialog.closeWithoutAnimation(this.dialogRef, 'close');
        return;
      }
      if (res) {
        this.dialog.closeWithoutAnimation(this.dialogRef, res);
      }
    });
    childRef.afterClosed().subscribe((res) => {
      if (!res || res === 'back') {
        this.dialog.show(this.dialogRef);
      }
    });
  }

  close(): void {
    this.dialogRef.close();
  }
}
